<template>
  <form :class="$style.container">
    <Button
      v-if="contactBack"
      :class="$style.backBtn"
      :text="config.labels.backBtn"
      @on-simple-click="handleBackClick"
    />
    <h1 v-if="contactTitle">{{ config.labels.contactTitle }} {{ contactTitle }}</h1>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.company.$model"
          :bold-label="true"
          :error="v$.formData.company.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.company.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.company.label"
          :original-value="previousData.company ? previousData.company : null"
        />
      </div>
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.name.$model"
          :bold-label="true"
          :error="v$.formData.name.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.name.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.name.label"
          :original-value="previousData.name ? previousData.name : null"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.streetNr.$model"
          :bold-label="true"
          :error="v$.formData.streetNr.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.streetNr.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.streetNr.label"
          :original-value="previousData.streetNr"
        />
      </div>
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.zipCity.$model"
          :bold-label="true"
          :error="v$.formData.zipCity.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.zipCity.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.zipCity.label"
          :original-value="previousData.zipCity"
        />
      </div>
    </div>
    <!--
      Use in FormInput
      :reset="true"
      to create a reset button at the right edge of any form filed.
      Example:
      <FormInput
      v-model.trim="v$.formData.phone.$model"
      :label="config.labels.phone.label"
      :optionalLabel="config.labels.phone.optionLabel"
      :error="false"
      :originalValue="previousData.phone"
      :formSubmitSwitch="formSubmitSwitch"
      :reset="true"
      /-->
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.phone.$model"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.phone.label"
          :optional-label="config.labels.phone.optionLabel"
          :original-value="previousData.phone"
        />
      </div>
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.fax.$model"
          :error="false"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.fax.label"
          :optional-label="config.labels.fax.optionLabel"
          :original-value="previousData.fax"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.email.$model"
          :bold-label="true"
          :error="v$.formData.email.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.email.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.email.label"
          :original-value="previousData.email"
        />
      </div>
      <div class="column" />
    </div>
    <div class="columns">
      <div class="column">
        <FormTextarea
          v-model.trim="v$.formData.message.$model"
          :columns="20"
          :error="v$.formData.message.$error && v$.$dirty && isSubmitted"
          :error-message="config.labels.message.errorMessage"
          :label="config.labels.message.label"
          :rows="5"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <FormInput
          v-model.trim="v$.formData.fileList.$model"
          :error="false"
          :error-message="config.labels.fileList.errorMessage"
          :form-submit-switch="formSubmitSwitch"
          :label="config.labels.fileList.label"
          :max-files-reached-label="config.labels.fileList.maxFilesReachedLabel"
          :no-files-label="config.labels.fileList.noFilesLabel"
          :optional-label="config.labels.fileList.optionLabel"
          :original-value="previousData.fileList"
          type="file"
          @on-file-list-changed="handleFileUpload"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column has-text-right-tablet">
        <Button :is-form="true" :text="config.labels.sendBtn" type="submit" @close="handleSubmit" />
      </div>
    </div>
    <Notifications
      :message="serverErrorMsg?.message || config.serverErrorMessage"
      :open="serverError"
      pattern="persistent"
      type="error"
    />
    <ReCaptcha :execute-switch="executeSwitch" :submit="submit" />
  </form>
</template>

<script setup>
import Button from '../Button/Button.vue';
import FormInput from '../FormInput/FormInput.vue';
import FormTextarea from '../FormTextarea/FormTextarea.vue';
import ReCaptcha from '../ReCaptcha/ReCaptcha.vue';

import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { onMounted, ref } from 'vue';
import { ContactFormEnum } from '../../utils/form';
import safeFetch from '../../utils/safeFetch';

import Notifications from '../Notifications/Notifications.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => {},
  },
});

const setServerError = (err) => {
  const error = err instanceof Error ? err : new Error(String(err));
  serverError.value = true;
  serverErrorMsg.value = error;
  console.error(error);
};
const parseContactFormResponse = async (response) => {
  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error('Server response is not valid JSON');
  }

  if (typeof data !== 'object' || data === null) {
    throw new Error(`Invalid response object: ${JSON.stringify(data)}`);
  }

  if (typeof data.success !== 'boolean') {
    throw new Error(`Missing or invalid "success" flag: ${JSON.stringify(data)}`);
  }

  if (data.success === false) {
    const message = data.error || data.errormessage || data.message || 'Unknown error from server';

    throw new Error(message);
  }

  return data;
};

const formData = ref({
  company: '',
  name: '',
  streetNr: '',
  zipCity: '',
  phone: '',
  fax: '',
  email: '',
  message: '',
  fileList: [],
  attachments: {
    name: 'Attachments',
    attachedFiles: [],
  },
});

const rules = {
  formData: {
    company: {
      required,
    },
    name: {
      required,
    },
    streetNr: {
      required,
    },
    zipCity: {
      required,
    },
    phone: {},
    fax: {},
    email: {
      required,
      email,
    },
    message: {
      required,
    },
    fileList: {},
  },
};

const previousData = ref({});
const isSubmitted = ref(false);
const formSubmitSwitch = ref(false);
const executeSwitch = ref(false);
const serverError = ref(false);
const serverErrorMsg = ref(null);
const contactTitle = ref(undefined);
const contactBack = ref(false);

const v$ = useVuelidate(rules, { formData });

onMounted(() => {
  const { search } = window.location;
  const params = new URLSearchParams(search);

  contactTitle.value = params.get(ContactFormEnum.contactTitle);
  formData.value.formEmail = params.get(ContactFormEnum.contactEmail);
  formData.value.distributorContact = params.get(ContactFormEnum.contactTitle);
  contactBack.value = params.get(ContactFormEnum.contactBack);
});

const handleFileUpload = (files) => {
  formData.value.attachments.attachedFiles = files;
};

const handleBackClick = () => {
  window.history.back();
};

const handleSubmit = () => {
  serverError.value = false;
  v$.value.$touch();
  isSubmitted.value = true;
  formSubmitSwitch.value = !formSubmitSwitch.value;

  if (!v$.value.$invalid) {
    executeSwitch.value = !executeSwitch.value;
    submit();
  } else {
    previousData.value = JSON.parse(JSON.stringify(formData.value));
  }
};

const submit = async (token) => {
  const { thankyouUrl } = props.config;
  const url = window.contactFormUrl;

  const {
    location: { origin: domain, pathname: pageUrl },
    currentLanguage: language,
    currentCountry: country,
  } = window;

  try {
    const response = await safeFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData.value,
        token,
        domain,
        pageUrl,
        language,
        country,
      }),
    });

    if (!response || response.status !== 200) {
      throw new Error(`HTTP error: ${response?.status}`);
    }

    await parseContactFormResponse(response);

    window.open(thankyouUrl, '_self');
  } catch (err) {
    setServerError(err);
  }
};
</script>

<style module lang="scss">
@forward './ContactForm.scss';
</style>
